/**
 * Tooltip formatter for typed `EchartsVizSpecV1` chart specs (ADR-0079).
 *
 * Post-ADR-0080: the `atlasTooltip` declarative-escape namespace and the
 * legacy `buildAtlasTooltipFormatter` config-object entry are gone. The
 * sole entry point is `buildTooltipFormatter(tooltipOpts, seriesList,
 * primaryFormat)` which takes the typed `CommonOptions.tooltip` directly.
 */

import { buildFormatter, type AtlasValueFormat } from "./valueFormat.js";
import type { CommonOptions, ValueFormat } from "./echartsSpec.js";

type TooltipParam = {
  seriesName: string;
  marker: string;
  value: unknown;
  seriesIndex: number;
  encode?: Record<string, number[]>;
  axisValueLabel?: string;
  axisValue?: unknown;
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function formatHeaderDate(
  item: TooltipParam | undefined,
  format: (v: unknown) => string,
): string {
  if (!item) return "";
  const raw = item.axisValue;
  // Only re-format if axisValue parses as a date; otherwise let the
  // ECharts-supplied axisValueLabel flow through unchanged.
  if (raw != null) {
    const d = raw instanceof Date ? raw : new Date(raw as string | number);
    if (!Number.isNaN(d.getTime())) return format(raw);
  }
  return String(item.axisValueLabel ?? "");
}

function extractNumericValue(p: TooltipParam): number | null {
  const v = p.value;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (Array.isArray(v)) {
    const yIndices = p.encode?.y;
    // When encode.y is set, this series's value lives at that exact index.
    // Trust it: don't fall back to scanning other cells, or a null at the
    // encoded position would leak the wrong series's value through the
    // hideNulls path.
    if (Array.isArray(yIndices) && yIndices.length > 0) {
      const val = v[yIndices[0]];
      if (typeof val === "number" && Number.isFinite(val)) return val;
      if (typeof val === "string") {
        const n = Number(val);
        return Number.isFinite(n) ? n : null;
      }
      return null;
    }
    for (let i = v.length - 1; i >= 0; i--) {
      if (typeof v[i] === "number" && Number.isFinite(v[i] as number)) return v[i] as number;
    }
  }
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Bridge a typed `ValueFormat` (echartsSpec.v1) to the existing
 * `AtlasValueFormat` shape consumed by `buildFormatter` in `valueFormat.ts`.
 * Kept so the renderer's tooltip and axis-label formatters reuse the
 * canonical formatting implementation (currency / percent / compact / date).
 */
export function valueFormatToLegacy(format: ValueFormat): AtlasValueFormat {
  switch (format.kind) {
    case "number":
      return {
        style: format.compact ? "compact" : "number",
        ...(format.precision !== undefined ? { decimals: format.precision } : {}),
      };
    case "currency":
      return {
        style: "currency",
        ...(format.currencyCode ? { currency: format.currencyCode } : {}),
        ...(format.precision !== undefined ? { decimals: format.precision } : {}),
      };
    case "percent":
      return {
        style: "percent",
        percentScale: "unit",
        ...(format.precision !== undefined ? { decimals: format.precision } : {}),
      };
    case "date":
      return {
        style: "date",
        ...(format.dateFormat ? { dateFormat: format.dateFormat } : {}),
      };
  }
}

/**
 * Construct an ECharts `tooltip.formatter` function from typed
 * `CommonOptions.tooltip`. Used by every per-chart-type renderer in
 * `lib/echartsRenderer/`.
 *
 * Defaults: `hideNulls: true`, `hideZeros: false`, `stackTotals: false`.
 *
 * `perSeriesFormats[i]` (if provided) overrides `primaryFormat` for the
 * series at that seriesIndex — needed for dual-axis/mixed-unit charts
 * where e.g. `y[0]` is currency and `y[1]` is a plain number.
 *
 * Returns `undefined` when no special handling is needed (no nulls to
 * suppress, no zero filter, no stack totals) — ECharts then uses its own
 * default tooltip rendering.
 */
export function buildTooltipFormatter(
  tooltipOpts: CommonOptions["tooltip"] | undefined,
  seriesList: Array<Record<string, unknown>>,
  primaryFormat?: ValueFormat,
  perSeriesFormats?: Array<ValueFormat | undefined>,
): ((params: unknown) => string) | undefined {
  const hideNulls = tooltipOpts?.hideNulls !== false; // default true
  const hideZeros = tooltipOpts?.hideZeros === true;
  const stackTotals = tooltipOpts?.stackTotals === true;
  const stackTotalLabels = tooltipOpts?.stackTotalLabels ?? {};

  // No special handling needed → let ECharts render its default tooltip.
  if (!hideNulls && !hideZeros && !stackTotals) return undefined;

  // Pre-compute series → stack name mapping when stack totals are requested.
  const stackMap = new Map<number, string>();
  const stackOrder: string[] = [];
  if (stackTotals) {
    const stackOrderSeen = new Set<string>();
    for (let i = 0; i < seriesList.length; i++) {
      const stack = seriesList[i].stack;
      if (typeof stack === "string" && stack.length > 0) {
        stackMap.set(i, stack);
        if (!stackOrderSeen.has(stack)) {
          stackOrderSeen.add(stack);
          stackOrder.push(stack);
        }
      }
    }
  }

  const fallbackFormat = primaryFormat
    ? buildFormatter(valueFormatToLegacy(primaryFormat))
    : ((v: unknown): string => String(v));
  // Custom date format for the tooltip header (axis value). Only formats when
  // the raw `axisValue` is a usable date — otherwise we fall back to the
  // ECharts-supplied `axisValueLabel`.
  const headerDateFormatter = tooltipOpts?.dateFormat
    ? buildFormatter({ style: "date", dateFormat: tooltipOpts.dateFormat })
    : null;
  const seriesFormatters = new Map<number, (v: unknown) => string>();
  const formatForSeries = (seriesIndex: number): ((v: unknown) => string) => {
    let f = seriesFormatters.get(seriesIndex);
    if (f) return f;
    const fmt = perSeriesFormats?.[seriesIndex];
    f = fmt ? buildFormatter(valueFormatToLegacy(fmt)) : fallbackFormat;
    seriesFormatters.set(seriesIndex, f);
    return f;
  };

  return (params: unknown): string => {
    const items = Array.isArray(params) ? (params as TooltipParam[]) : [params as TooltipParam];

    const headerText = headerDateFormatter
      ? formatHeaderDate(items[0], headerDateFormatter)
      : String(items[0]?.axisValueLabel ?? "");
    let html = `<div style="margin-bottom:4px;font-weight:600">${escapeHtml(headerText)}</div>`;

    const stackSums = new Map<string, number>();

    // Group items: stacked first (grouped by stack), then non-stacked.
    const stackedItems: Array<{ param: TooltipParam; stack: string }> = [];
    const unstackedItems: TooltipParam[] = [];
    for (const p of items) {
      const stackName = stackMap.get(p.seriesIndex);
      if (stackName != null) {
        stackedItems.push({ param: p, stack: stackName });
      } else {
        unstackedItems.push(p);
      }
    }

    // Render stacked series grouped by stack, with total after each group.
    for (const stackName of stackOrder) {
      const groupItems = stackedItems.filter((si) => si.stack === stackName);
      if (groupItems.length === 0) continue;

      let stackTotalFormat: ((v: unknown) => string) | null = null;
      let stackTotalFormatConflict = false;
      for (const { param: p } of groupItems) {
        const rawVal = extractNumericValue(p);
        if (hideNulls && rawVal == null) continue;
        if (hideZeros && rawVal === 0) continue;
        const seriesFormat = formatForSeries(p.seriesIndex);
        if (stackTotalFormat == null) stackTotalFormat = seriesFormat;
        else if (stackTotalFormat !== seriesFormat) stackTotalFormatConflict = true;
        const display = rawVal != null ? seriesFormat(rawVal) : "";
        html += `<div style="display:flex;justify-content:space-between;align-items:center;gap:20px">`
          + `<span>${p.marker ?? ""} ${escapeHtml(p.seriesName ?? "")}</span>`
          + `<span style="font-weight:600">${display}</span></div>`;

        if (rawVal != null) {
          stackSums.set(stackName, (stackSums.get(stackName) ?? 0) + rawVal);
        }
      }

      const total = stackSums.get(stackName);
      if (total != null) {
        const label = stackTotalLabels[stackName] ?? "Total";
        const totalFormat =
          stackTotalFormatConflict || stackTotalFormat == null ? fallbackFormat : stackTotalFormat;
        html += `<div style="border-top:1px solid rgba(0,0,0,0.15);margin-top:2px;padding-top:2px;display:flex;justify-content:space-between;align-items:center;gap:20px;font-weight:600">`
          + `<span>${escapeHtml(label)}</span>`
          + `<span>${totalFormat(total)}</span></div>`;
      }
    }

    // Render non-stacked series after a separator (if there were stacked items).
    if (unstackedItems.length > 0 && stackedItems.length > 0) {
      html += `<div style="border-top:1px solid rgba(0,0,0,0.08);margin-top:4px;padding-top:4px"></div>`;
    }
    for (const p of unstackedItems) {
      const rawVal = extractNumericValue(p);
      if (hideNulls && rawVal == null) continue;
      if (hideZeros && rawVal === 0) continue;
      const seriesFormat = formatForSeries(p.seriesIndex);
      const display = rawVal != null ? seriesFormat(rawVal) : "";
      html += `<div style="display:flex;justify-content:space-between;align-items:center;gap:20px">`
        + `<span>${p.marker ?? ""} ${escapeHtml(p.seriesName ?? "")}</span>`
        + `<span style="font-weight:600">${display}</span></div>`;
    }

    return html;
  };
}
